import { useEffect, useState } from 'react';
import { Box, ButtonBase, Paper, Stack, Typography } from '@mui/material';
import { Loading } from 'framework/ui/mui-builder';
import { useCoreServiceClient } from '../../api/coreServiceClient';
import useAsyncData from '../../hooks/useAsyncData';
import EmptyState from '../../components/EmptyState';
import ErrorNotice from '../../components/ErrorNotice';
import OriginDot from '../../components/OriginDot';
import SplitBar from '../../components/SplitBar';
import type { NodeCardDto } from '../../types';
import { formatNumber } from '../../utils/format';
import NodePanel from './NodePanel';

const BusinessFunctionButton = ({
  card,
  isActive,
  onClick,
}: {
  card: NodeCardDto;
  isActive: boolean;
  onClick: () => void;
}) => {
  const { stats } = card;

  return (
    <ButtonBase
      onClick={onClick}
      aria-pressed={isActive}
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: '2px 8px',
        width: '100%',
        padding: '10px 12px',
        border: 1,
        borderColor: isActive ? 'primary.main' : 'border.default',
        borderRadius: 1,
        boxShadow: isActive ? theme => `inset 0 0 0 1px ${theme.palette.primary.main}` : 'none',
        backgroundColor: 'background.paper',
        textAlign: 'left',
        '&:hover': { borderColor: isActive ? 'primary.main' : 'text.secondary' },
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {card.title}
      </Typography>
      <Typography variant="subtitle2" sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        {formatNumber(stats.open)}
      </Typography>
      <Stack direction="row" gap={1.25} flexWrap="wrap" sx={{ gridColumn: '1 / -1' }}>
        {stats.overdue ? (
          <Typography variant="caption" color="error.main">
            {formatNumber(stats.overdue)} overdue
          </Typography>
        ) : null}
        {stats.queued ? (
          <Typography variant="caption" color="text.secondary">
            {formatNumber(stats.queued)} queued
          </Typography>
        ) : null}
        {stats.unowned ? (
          <Typography variant="caption" color="warning.main">
            {formatNumber(stats.unowned)} no owner
          </Typography>
        ) : null}
        {!stats.open ? (
          <Typography variant="caption" color="text.secondary">
            no open tickets
          </Typography>
        ) : null}
      </Stack>
      {stats.open ? (
        <Box sx={{ gridColumn: '1 / -1' }}>
          <SplitBar fromProcesses={stats.fromProcesses} fromPeople={stats.fromPeople} />
        </Box>
      ) : null}
    </ButtonBase>
  );
};

const TreeTab = () => {
  const client = useCoreServiceClient();
  const overview = useAsyncData(() => client.getTreeOverview(), [client]);
  const [nodeId, setNodeId] = useState<Nullable<string>>(null);
  const data = overview.data;

  useEffect(() => {
    if (!nodeId && data?.businessFunctions.length) {
      setNodeId(data.businessFunctions[0].id);
    }
  }, [data, nodeId]);

  if (overview.error && !data) {
    return <ErrorNotice error={overview.error} onRetry={overview.reload} />;
  }

  if (!data) {
    return <Loading isInitialPosition />;
  }

  const root = data.root.stats;

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '300px 1fr' }, gap: 2.5, alignItems: 'start' }}>
      <Box component="aside">
        <Paper variant="outlined" sx={{ padding: '12px 14px', marginBottom: 1.25 }}>
          <Typography variant="h6">{data.root.title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {formatNumber(root.open)} open tickets ·{' '}
            <Box component="span" sx={{ color: 'error.main' }}>
              {formatNumber(root.overdue)} overdue
            </Box>{' '}
            · {formatNumber(root.queued)} in queues · {formatNumber(root.unowned)} without owner
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ marginTop: 0.5 }}>
            <OriginDot kind="process" />
            {formatNumber(root.fromProcesses)} from {formatNumber(root.processRuns)} process runs ·{' '}
            <OriginDot kind="person" />
            {formatNumber(root.fromPeople)} from people
          </Typography>
        </Paper>
        <Stack gap={0.75} sx={{ maxHeight: { md: 'calc(100vh - 220px)' }, overflow: 'auto' }}>
          {data.businessFunctions.map(card => (
            <BusinessFunctionButton
              key={card.id}
              card={card}
              isActive={card.id === nodeId}
              onClick={() => setNodeId(card.id)}
            />
          ))}
        </Stack>
      </Box>
      {nodeId ? (
        <NodePanel key={nodeId} nodeId={nodeId} onOpenNode={setNodeId} />
      ) : (
        <Paper variant="outlined">
          <EmptyState>Select a business function</EmptyState>
        </Paper>
      )}
    </Box>
  );
};

export default TreeTab;
